import type { LabPriority, LabProject, LabSubtask, LabTestCategory } from './types';

/** Lowercase keyword sniff lists, ported from v56. */
const SAFETY_KEYWORDS = [
  'thermal',
  'hipot',
  'temperature',
  'leakage',
  'dielectric',
  'ground bond',
  'bonding',
  'ground continuity',
  'damp',
  'rain',
  'wet',
  'abnormal',
  'normal temp',
  'push',
  'pull',
  'strain relief',
  'knockout',
  'stability',
  'drop test',
  'glow wire',
  'ball pressure',
  'ul1598',
  'ul1574',
  'ul2108',
  'ul8750',
  'intertek',
  'safety',
];

const PHOTO_KEYWORDS = [
  'gonio',
  'goniophotometer',
  'sphere',
  'dimming',
  'dim',
  'ies',
  'lumen',
  'photometric',
  'efficacy',
  'cct',
  'cri',
];

export function inferSubCategory(st: LabSubtask): LabTestCategory | null {
  if (st.testCategory) return st.testCategory;
  const n = (st.name || '').toLowerCase();
  if (SAFETY_KEYWORDS.some((k) => n.includes(k))) return 'safety';
  if (/dimming|dimmer|flicker|\bdim\b/.test(n)) return 'dimming';
  if (PHOTO_KEYWORDS.some((k) => n.includes(k))) return 'photometric';
  return null;
}

/** Display labels — identical to Request a Test's test types. */
export const LAB_CATEGORY_LABEL: Record<LabTestCategory, string> = {
  safety: '🛡️ Safety',
  photometric: '💡 Photometric / Performance',
  dimming: '🔆 Dimming',
  both: '⚡ Multiple tests',
};

/** Options for category pickers (drawer, Add test). */
export const LAB_CATEGORY_OPTIONS: { value: LabTestCategory | ''; label: string }[] = [
  { value: '', label: '—' },
  { value: 'safety', label: LAB_CATEGORY_LABEL.safety },
  { value: 'photometric', label: LAB_CATEGORY_LABEL.photometric },
  { value: 'dimming', label: LAB_CATEGORY_LABEL.dimming },
  { value: 'both', label: LAB_CATEGORY_LABEL.both },
];

/**
 * The test type to SHOW for a lab test: the category chosen on the test (the
 * same four as Request a Test). Only older tests with no category fall back
 * to guessing from their subtasks.
 */
export function displayCategory(p: LabProject): LabTestCategory | null {
  if (p.test_category) return p.test_category;
  const cats = projectCategories(p);
  cats.delete('both');
  if (cats.size > 1) return 'both';
  const only = Array.from(cats)[0];
  return only ?? null;
}

export function projectCategories(p: LabProject): Set<LabTestCategory> {
  const cats = new Set<LabTestCategory>();
  const subs = p.subtasks ?? [];
  const active = subs.filter((s) => s.status !== 'Completed' && s.status !== 'Cancelled');
  active.forEach((s) => {
    const c = inferSubCategory(s);
    if (c) cats.add(c);
  });
  if (cats.size === 0) {
    subs.forEach((s) => {
      const c = inferSubCategory(s);
      if (c) cats.add(c);
    });
  }
  if (cats.size === 0 && p.test_category) cats.add(p.test_category);
  return cats;
}

/** Build a stable initials string from an email, falling back to '??'. */
export function emailInitials(email: string | null | undefined): string {
  if (!email) return '??';
  const local = email.split('@')[0] ?? '';
  if (!local) return '??';
  // jpenaverde → JP, dselani → DS, abhatnagar → AB
  return (local[0] + (local[1] ?? '')).toUpperCase();
}

export function emailShortName(email: string | null | undefined): string {
  if (!email) return '—';
  return email.split('@')[0] ?? email;
}

/** YYYY-MM-DD → "May 28" or '—' for blank. Past due → returns flag too. */
export function formatDueShort(iso: string | null | undefined): {
  text: string;
  overdue: boolean;
} {
  if (!iso) return { text: '—', overdue: false };
  const dt = new Date(iso + 'T00:00:00');
  if (Number.isNaN(dt.getTime())) return { text: iso, overdue: false };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return {
    text: dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    overdue: dt < today,
  };
}

const PRIORITY_RANK: Record<LabPriority, number> = {
  Urgent: 0,
  High: 1,
  Normal: 2,
  Low: 3,
};

export function sortByPriority(a: LabProject, b: LabProject): number {
  const ra = PRIORITY_RANK[a.priority] ?? 9;
  const rb = PRIORITY_RANK[b.priority] ?? 9;
  if (ra !== rb) return ra - rb;
  // Then by due date ascending, nulls last
  if (a.due_date && b.due_date) return a.due_date < b.due_date ? -1 : 1;
  if (a.due_date) return -1;
  if (b.due_date) return 1;
  return 0;
}

export function subtaskProgress(p: LabProject): { done: number; total: number } {
  const subs = p.subtasks ?? [];
  const total = subs.length;
  const done = subs.filter((s) => s.status === 'Completed').length;
  return { done, total };
}
